/* eslint-disable */
/**
 * faceengine.js — everything the kiosk and the enrolment page know about a camera frame.
 *
 *   FaceEngine      detection policy + landmarks + head pose + eye openness + quality + descriptor
 *   ProbeCollector  keeps the best few face samples seen during one attempt
 *
 * The same file runs in the browser (global `faceapi`, <video> input) and in Node tests (the node-wasm build
 * of face-api.js, tensor input), through a tiny "env" adapter. That is what lets the real detection policy be
 * tested on real photographs instead of only trusted.
 *
 * Detector policy (measured on real photos, see test/pipeline.test.js):
 *   TinyFaceDetector at input size 224 finds faces that fill 15-40% of the frame width (the normal kiosk
 *   distance) every time and costs ~10-30 ms; larger sizes get WORSE on close-ups. When nothing is found we
 *   try size 416 so a person standing far away can still be told to "come closer".
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./liveness-core'), require('./quality'));
  } else root.FaceEngine = factory(root.LivenessCore, root.FaceQuality);
})(typeof self !== 'undefined' ? self : this, function (LC, FQ) {
  'use strict';

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const round = (v, d = 3) => (Number.isFinite(v) ? Number(v.toFixed(d)) : v);

  class FaceEngine {
    /**
     * @param faceapi  the face-api.js namespace
     * @param env      { videoSize(input) -> {w,h}, readRegion(input, box) -> {data: RGBA, w, h} }
     */
    constructor(faceapi, env, options = {}) {
      this.fa = faceapi;
      this.env = env;
      this.opt = Object.assign({ trackSize: 224, searchSize: 416, scoreThreshold: 0.5, otherFaceRatio: 0.4 }, options);
    }

    /** source: a URL string (browser) or { dir } (Node). */
    async load(source) {
      const nets = this.fa.nets;
      const list = [nets.tinyFaceDetector, nets.faceLandmark68Net, nets.faceRecognitionNet, nets.faceExpressionNet, nets.ageGenderNet];
      await Promise.all(list.map((n) => (typeof source === 'string' ? n.loadFromUri(source) : n.loadFromDisk(source.dir))));
    }

    _det(size) { return new this.fa.TinyFaceDetectorOptions({ inputSize: size, scoreThreshold: this.opt.scoreThreshold }); }

    async _detect(input, size, expressions) {
      let task = this.fa.detectAllFaces(input, this._det(size)).withFaceLandmarks();
      if (expressions) task = task.withFaceExpressions();
      return task;
    }

    /**
     * Analyse one frame.
     * @param input  <video>/<canvas> in the browser, a tf.Tensor3D in Node
     * @param o      { search: also try the large detector when nothing is found,
     *                 expressions: compute smile probability, descriptor: compute the 128-D descriptor (true, or a predicate (mainFace, videoSize) => bool evaluated after quality),
     *                 quality: measure sharpness/brightness of the face crop,
     *                 focus: {x, y} pick the face nearest this point instead of the largest (tests / tracking) }
     * @returns { count, main, others, ms, detector }  main = null when no face
     */
    async analyze(input, o = {}) {
      const t0 = now();
      const vs = this.env.videoSize(input);
      let size = this.opt.trackSize;
      let raw = await this._detect(input, size, !!o.expressions);
      if (!raw.length && o.search) { size = this.opt.searchSize; raw = await this._detect(input, size, !!o.expressions); }
      const items = raw.map((r) => ({ r, f: this._summarise(r, vs) }));
      if (o.focus) {
        const d = (i) => Math.hypot(i.f.cx - o.focus.x, i.f.cy - o.focus.y) / i.f.box.w;
        items.sort((a, b) => d(a) - d(b));
      } else items.sort((a, b) => b.f.box.w * b.f.box.h - a.f.box.w * a.f.box.h);
      const faces = items.map((i) => i.f);
      const main = faces[0] || null;
      let count = 0;
      if (main) {
        // "other people" = faces at least otherFaceRatio as big (linear) as the main one; tiny background detections are ignored
        const minArea = main.box.w * main.box.h * this.opt.otherFaceRatio * this.opt.otherFaceRatio;
        count = faces.filter((f) => f === main || f.box.w * f.box.h >= minArea).length;
        if (o.quality) {
          const reg = await this.env.readRegion(input, main.box);
          const q = FQ.measureRegion(reg.data, reg.w, reg.h);
          main.sharp = round(q.sharp, 1); main.bright = round(q.bright, 1);
        }
        // o.descriptor may be a predicate (face) => bool, so the kiosk only pays for the 128-D network on frames it will keep
        if (o.descriptor && (typeof o.descriptor !== 'function' || o.descriptor(main, vs))) main.descriptor = Array.from(await this._descriptor(input, items[0].r));
      }
      return { count, main, faces, ms: now() - t0, detector: size, video: vs };
    }

    _summarise(r, vs) {
      const pts = r.landmarks.positions;
      const b = r.detection.box;
      const box = { x: b.x, y: b.y, w: b.width, h: b.height };
      const face = {
        box, score: round(r.detection.score), landmarks: pts,
        ear: round(LC.earFromLandmarks(pts)), yaw: round(LC.yawFromLandmarks(pts)),
        pitch: round(LC.pitchFromLandmarks(pts)), roll: round(LC.rollFromLandmarks(pts), 1),
        widthRatio: round(box.w / vs.w),
        cx: round(box.x + box.w / 2, 1), cy: round(box.y + box.h / 2, 1),
        smile: r.expressions ? round(r.expressions.happy) : null,
        expressions: r.expressions || null,
      };
      return face;
    }

    /** 128-D descriptor of the aligned face (dlib-style alignment, identical to withFaceDescriptors()). */
    async _descriptor(input, raw) {
      const rect = raw.landmarks.align(null, { useDlibAlignment: true });
      const isTensor = this.fa.tf && input instanceof this.fa.tf.Tensor;
      const crops = isTensor ? await this.fa.extractFaceTensors(input, [rect]) : await this.fa.extractFaces(input, [rect]);
      try {
        return await this.fa.nets.faceRecognitionNet.computeFaceDescriptor(crops[0]);
      } finally {
        if (isTensor) crops.forEach((t) => t.dispose());
      }
    }

    /**
     * One-off "understanding" pass for the main face: age, gender, expression. Slower than analyze(); call it
     * once per attempt, not per frame.
     */
    async attributes(input) {
      const r = await this.fa.detectAllFaces(input, this._det(this.opt.trackSize)).withFaceLandmarks().withFaceExpressions().withAgeAndGender();
      if (!r.length) return null;
      r.sort((a, b) => b.detection.box.width * b.detection.box.height - a.detection.box.width * a.detection.box.height);
      const m = r[0];
      const top = Object.entries(m.expressions).sort((a, b) => b[1] - a[1])[0];
      return {
        age: round(m.age, 1), gender: m.gender, genderProb: round(m.genderProbability, 2),
        emotion: top[0], emotionProb: round(top[1], 2), expressions: m.expressions,
      };
    }
  }

  /**
   * Keeps the best few face samples of one attempt. A sample is only worth the time of computing a descriptor
   * when the frame is frontal, sharp, bright, large enough, and far enough in time from the previous sample.
   */
  class ProbeCollector {
    constructor(thresholds, { max = 7, minGapMs = 230, frontalYaw } = {}) {
      this.t = thresholds;
      this.max = max;
      this.minGapMs = minGapMs;
      this.frontalYaw = frontalYaw || LC.DEFAULTS.frontalYaw;
      this.items = [];
      this.lastAt = -1e9;
    }
    meta(face, videoW, t) {
      return { score: face.score, w: face.box.w, videoW, sharp: face.sharp, bright: face.bright, yaw: face.yaw, t };
    }
    issues(face, videoW) {
      if (face.sharp === undefined) return ['not measured'];
      return LC.probeIssues(this.meta(face, videoW, 0), this.t, { frontalYaw: this.frontalYaw });
    }
    rank(m) {
      const sharpN = Math.min(1, m.sharp / Math.max(1, this.t.minSharpness * 4));
      return m.score * 40 + sharpN * 30 + (1 - Math.min(1, Math.abs(m.yaw) / this.frontalYaw)) * 30;
    }
    /** Should we spend time computing a descriptor for this frame? */
    wants(face, videoW, t) {
      if (t - this.lastAt < this.minGapMs) return false;
      if (this.issues(face, videoW).length) return false;
      if (this.items.length < this.max) return true;
      return this.rank(this.meta(face, videoW, t)) > Math.min(...this.items.map((i) => i.rank));
    }
    add(face, videoW, t) {
      const m = this.meta(face, videoW, t);
      const item = Object.assign({ descriptor: face.descriptor, rank: this.rank(m) }, m);
      this.items.push(item);
      this.lastAt = t;
      if (this.items.length > this.max) {
        this.items.sort((a, b) => a.rank - b.rank);
        this.items.shift();
      }
    }
    count() { return this.items.length; }
    /** Samples in time order, without the internal rank. */
    export() {
      return [...this.items].sort((a, b) => a.t - b.t).map((i) => ({
        descriptor: i.descriptor.map((x) => Number(x.toFixed(6))), score: i.score, w: round(i.w, 1), videoW: i.videoW,
        sharp: round(i.sharp, 1), bright: round(i.bright, 1), yaw: i.yaw, t: Math.round(i.t),
      }));
    }
  }

  // ---- browser adapter ----------------------------------------------------------------------------------
  function browserEnv() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return {
      videoSize(input) { return { w: input.videoWidth || input.width, h: input.videoHeight || input.height }; },
      readRegion(input, box) {
        const vs = this.videoSize(input);
        const x = Math.max(0, Math.floor(box.x)), y = Math.max(0, Math.floor(box.y));
        const w = Math.max(2, Math.min(vs.w - x, Math.ceil(box.w))), h = Math.max(2, Math.min(vs.h - y, Math.ceil(box.h)));
        canvas.width = w; canvas.height = h;
        ctx.drawImage(input, x, y, w, h, 0, 0, w, h);
        return { data: ctx.getImageData(0, 0, w, h).data, w, h };
      },
    };
  }

  return { FaceEngine, ProbeCollector, browserEnv, round };
});
